import { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, doc, getDoc, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Package, Search, Tag, Eye, ClipboardList, ChevronLeft, ChevronRight, ShoppingCart, ArrowUp, FileDown, Link2, Copy, ExternalLink, TrendingUp, Star, Zap, Users, ArrowRight, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../../contexts/CartContext';
import { toast } from '../../../components/ui/use-toast';

interface Product {
  id: string;
  name: string;
  description?: string;
  sku: string;
  ean?: string;
  regularPrice: number;
  salePrice: number;
  mainImage?: string;
  category: string | null;
  isPrivateSale: boolean;
  companyId: string;
  promotionalPeriod?: {
    startDate: string;
    endDate: string;
  };
  isProPlus?: boolean;
  proPlusPrice?: number;
  status: string;
  stock: number;
  createdAt?: any;
}

interface Company {
  id: string;
  commercialName: string;
  legalName: string;
}

interface ProductWithCompany extends Product {
  companyName: string;
  companyData?: Company;
}

interface Category {
  id: string;
  name: string;
  type: string;
  productCount?: number;
}

const ITEMS_PER_PAGE = 20;

// Componente ProductCard para exibir produtos de forma consistente
const ProductCard: React.FC<{ product: ProductWithCompany }> = ({ product }) => {
  const navigate = useNavigate();
  const { addItem } = useCart();

  const handleAddToCart = (product: ProductWithCompany) => {
    // Debug: verificar os valores do produto
    console.log('Produto completo:', product);
    console.log('proPlusPrice:', product.proPlusPrice, typeof product.proPlusPrice);
    console.log('regularPrice:', product.regularPrice, typeof product.regularPrice);
    
    // Garantir que temos um preço válido - usar sempre o preço de revenda (proPlusPrice)
    let price = 0;
    
    if (product.proPlusPrice && typeof product.proPlusPrice === 'number' && !isNaN(product.proPlusPrice)) {
      price = product.proPlusPrice;
    } else if (product.regularPrice && typeof product.regularPrice === 'number' && !isNaN(product.regularPrice)) {
      price = product.regularPrice;
    } else {
      // Fallback: tentar converter strings para números
      const proPlusNum = parseFloat(String(product.proPlusPrice));
      const regularNum = parseFloat(String(product.regularPrice));
      
      if (!isNaN(proPlusNum)) {
        price = proPlusNum;
      } else if (!isNaN(regularNum)) {
        price = regularNum;
      } else {
        price = 0; // Último recurso
      }
    }

    console.log('Preço final calculado:', price);

    if (price <= 0) {
      toast({
        title: "Erro",
        description: "Não foi possível determinar o preço do produto.",
        variant: "destructive"
      });
      return;
    }

    addItem({
      productId: product.id,
      name: product.name,
      price: price,
      mainImage: product.mainImage || '',
      quantity: 1,
      companyId: product.companyId,
      companyName: product.companyName || 'Empresa não encontrada'
    });
    
    toast({
      title: "Produto adicionado ao carrinho",
      description: `${product.name} foi adicionado ao seu carrinho.`,
    });
  };

  const getCategoryName = (categoryId: string | null): string => {
    if (!categoryId) return 'Sem categoria';
    // Esta função será definida no componente principal
    return 'Categoria';
  };

  const isPromotionalPriceActive = (product: Product): boolean => {
    if (!product.promotionalPeriod?.startDate || !product.promotionalPeriod?.endDate) {
      return false;
    }

    const now = new Date().getTime();
    const startDate = new Date(product.promotionalPeriod.startDate).getTime();
    const endDate = new Date(product.promotionalPeriod.endDate).getTime();

    return now >= startDate && now <= endDate;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
      {/* Imagem do Produto */}
      <div 
        onClick={() => navigate(`/dashboard/drop/${product.id}`)}
        className="relative aspect-square overflow-hidden cursor-pointer"
      >
        <img
          src={product.mainImage}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <Eye size={24} className="text-white opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300" />
        </div>
        
        {/* Badge de Stock */}
        <div className="absolute top-3 left-3">
          <span className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium text-gray-700">
            Stock: {product.stock}
          </span>
        </div>
      </div>

      {/* Informações do Produto */}
      <div className="p-4">
        {/* Nome do Produto */}
        <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2 min-h-[2.5rem]">
          {product.name}
        </h3>

        {/* Preços */}
        <div className="space-y-2 mb-4">
          {/* Preço de Revenda */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Revenda:</span>
            <span className="text-lg font-bold text-secondary">
              {(product.proPlusPrice || product.regularPrice).toFixed(2)} €
            </span>
          </div>
          
          {/* Preço PVPR */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">PVPR:</span>
            <span className="text-sm text-gray-900">
              {isPromotionalPriceActive(product) ? product.salePrice.toFixed(2) : product.regularPrice.toFixed(2)} €
            </span>
          </div>
          
          {/* Lucro potencial */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Lucro:</span>
            <span className="text-sm font-bold text-primary">
              {(
                (isPromotionalPriceActive(product) ? product.salePrice : product.regularPrice) - 
                (product.proPlusPrice || product.regularPrice)
              ).toFixed(2)} €
            </span>
          </div>
        </div>
        
        {/* Botões de ação */}
        <div className="flex gap-2">
          <button
            onClick={() => handleAddToCart(product)}
            className="flex-1 bg-primary hover:bg-primary-dark text-secondary px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <ShoppingCart size={16} />
            Carrinho
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AreaDrop() {
  // Referência para o topo da página
  const topRef = useRef<HTMLDivElement>(null);
  
  const [products, setProducts] = useState<ProductWithCompany[]>([]);
  const [recentProducts, setRecentProducts] = useState<ProductWithCompany[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [topCategories, setTopCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showXmlModal, setShowXmlModal] = useState(false);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const handleCopyXmlLink = () => {
    // Get the base URL from window.location
    const baseUrl = window.location.origin;
    const xmlUrl = `${baseUrl}/api/products/xml`;
    
    navigator.clipboard.writeText(xmlUrl);
    toast({
      title: 'Link copiado!',
      description: 'O link do XML foi copiado para a área de transferência.',
      duration: 3000,
    });
  };

  const handleOpenXmlLink = () => {
    const baseUrl = window.location.origin;
    const xmlUrl = `${baseUrl}/api/products/xml`;
    window.open(xmlUrl, '_blank');
  };

  const handleExportCSV = () => {
    // Preparar os dados para o CSV - usar todos os produtos em vez de apenas os filtrados
    const csvData = products.map(product => ({
      nome: product.name,
      descricao: product.description || '',
      preco_revenda: product.proPlusPrice.toFixed(2),
      pvpr: (isPromotionalPriceActive(product) ? product.salePrice : product.regularPrice).toFixed(2),
      lucro: ((isPromotionalPriceActive(product) ? product.salePrice : product.regularPrice) - product.proPlusPrice).toFixed(2),
      imagem: product.mainImage || '',
      ean: product.ean || '',
      stock: product.stock,
      categoria: getCategoryName(product.category)
    }));

    // Criar o conteúdo do CSV
    const headers = ['Nome', 'Descrição', 'Preço Revenda', 'PVPR', 'Lucro', 'Link Imagem', 'EAN', 'Stock', 'Categoria'];
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => [
        `"${row.nome.replace(/"/g, '""')}"`,
        `"${row.descricao.replace(/"/g, '""')}"`,
        row.preco_revenda,
        row.pvpr,
        row.lucro,
        `"${row.imagem}"`,
        `"${row.ean}"`,
        row.stock,
        `"${row.categoria}"`
      ].join(','))
    ].join('\n');

    // Criar e fazer download do arquivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'todos_produtos_drop.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "CSV Exportado",
      description: `${csvData.length} produtos exportados com sucesso.`,
    });
  };

  useEffect(() => {
    if (!currentUser) {
      console.log('Sem usuário atual');
      navigate('/dashboard/login');
      return;
    }

    console.log('Usuário atual:', {
      uid: currentUser.uid,
      companyId: currentUser.companyId,
      email: currentUser.email
    });

    const initializeData = async () => {
      await loadData();
    };

    initializeData();
  }, [currentUser]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm]);

  const loadCompanyData = async (companyId: string) => {
    try {
      const companyRef = doc(db, 'companies', companyId);
      const companyDoc = await getDoc(companyRef);
      
      if (companyDoc.exists()) {
        return companyDoc.data();
      }
      console.log('Empresa não encontrada:', companyId);
      return null;
    } catch (err) {
      console.error('Erro ao carregar dados da empresa:', companyId, err);
      return null;
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesRef = collection(db, 'categories');
      const snapshot = await getDocs(categoriesRef);
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        type: doc.data().type || 'normal'
      }));
      console.log('Todas as categorias carregadas:', categoriesData);
      setAllCategories(categoriesData);
      return categoriesData;
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
      return [];
    }
  };

  const loadData = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError('');

      // Obter todos os produtos ordenados por data de criação (mais recentes primeiro)
      const productsRef = collection(db, 'products');
      const productsQuery = query(productsRef, orderBy('createdAt', 'desc'));
      const productsSnapshot = await getDocs(productsQuery);
      
      console.log('Total de produtos:', productsSnapshot.size);

      // Filtrar produtos em memória
      const filteredDocs = productsSnapshot.docs.filter(doc => {
        const data = doc.data();
        const isApproved = data.status === 'approved';
        const hasStock = (data.stock || 0) > 0;
        const hasBaselinkerID = !!data.baselinker_id; // Verifica se baselinker_id está preenchido
        const isProPlus = data.isProPlus === true; // Verifica se isProPlus é true
        const hasProPlusPrice = !!data.proPlusPrice && data.proPlusPrice > 0; // Verifica se proPlusPrice tem valor
        
        // Log detalhado para debug
        console.log('Verificando produto:', {
          id: doc.id,
          name: data.name,
          baselinker_id: data.baselinker_id,
          isProPlus: data.isProPlus,
          proPlusPrice: data.proPlusPrice,
          hasStock,
          hasBaselinkerID,
          isProPlus,
          hasProPlusPrice,
          shouldShow: isApproved && hasStock && hasBaselinkerID && isProPlus && hasProPlusPrice
        });
        
        return isApproved && hasStock && hasBaselinkerID && isProPlus && hasProPlusPrice;
      });

      // Identificar as categorias que têm produtos associados
      const categoriesWithProducts = new Set<string>();
      const categoryProductCount = new Map<string, number>();
      
      filteredDocs.forEach(doc => {
        const data = doc.data();
        if (data.category) {
          categoriesWithProducts.add(data.category);
          categoryProductCount.set(data.category, (categoryProductCount.get(data.category) || 0) + 1);
        }
      });
      
      console.log('Categorias com produtos:', Array.from(categoriesWithProducts));
      
      // Carregar e filtrar categorias
      const categoriesRef = collection(db, 'categories');
      const categoriesSnapshot = await getDocs(categoriesRef);
      const allCategoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        type: doc.data().type || 'normal',
        productCount: categoryProductCount.get(doc.id) || 0
      }));

      // Filtrar as categorias para mostrar apenas aquelas com produtos associados
      const filteredCategories = allCategoriesData.filter(category => 
        categoriesWithProducts.has(category.id)
      );

      // Obter top 6 categorias com mais produtos
      const topCategoriesData = filteredCategories
        .sort((a, b) => (b.productCount || 0) - (a.productCount || 0))
        .slice(0, 6);
      
      // Atualizar os estados das categorias
      setAllCategories(allCategoriesData);
      setCategories(filteredCategories);
      setTopCategories(topCategoriesData);
      
      // Não aplicamos paginação aqui, vamos processar TODOS os produtos
      // para que a pesquisa funcione em todas as páginas
      console.log('Total após filtros:', filteredDocs.length);
      console.log('Categorias filtradas:', filteredCategories.length);
      
      // Vamos processar todos os produtos, não apenas os da página atual
      // A paginação será aplicada depois da pesquisa e filtros

      const productsPromises = filteredDocs.map(async (doc) => {
        const data = doc.data();
        console.log('Produto carregado:', {
          id: doc.id,
          name: data.name,
          category: data.category,
          companyId: data.companyId,
          isPrivateSale: data.isPrivateSale
        });
        
        const productData: Product = {
          id: doc.id,
          name: data.name || '',
          sku: data.sku || '',
          regularPrice: Number(data.regularPrice) || 0,
          salePrice: Number(data.salePrice) || 0,
          companyId: data.companyId || '',
          isPrivateSale: true,
          isProPlus: Boolean(data.isProPlus),
          proPlusPrice: Number(data.proPlusPrice) || 0,
          description: data.description,
          ean: data.ean,
          mainImage: data.mainImage || data.baselinkerMainImage,
          category: data.category || null,
          promotionalPeriod: data.promotionalPeriod,
          status: data.status,
          stock: data.stock,
          createdAt: data.createdAt
        };

        try {
          // Não carregamos dados da empresa para manter o anonimato do vendedor
          const product = {
            ...productData,
            companyName: 'Anônimo', // Nome do vendedor anônimo
            companyData: null // Não incluímos dados da empresa
          };
          console.log('Produto processado:', {
            id: product.id,
            name: product.name,
            category: product.category,
            companyName: product.companyName
          });
          return product;
        } catch (err) {
          console.error('Error loading company data for product:', productData.id, err);
          return {
            ...productData,
            companyName: 'Empresa não encontrada'
          };
        }
      });

      console.log('Processando produtos...');
      const productsData = await Promise.all(productsPromises);
      console.log('Total de produtos processados:', productsData.length);
      
      // Debug: verificar os preços dos primeiros produtos
      productsData.slice(0, 3).forEach((product, index) => {
        console.log(`Produto ${index + 1}:`, {
          name: product.name,
          regularPrice: product.regularPrice,
          proPlusPrice: product.proPlusPrice,
          typeRegular: typeof product.regularPrice,
          typeProPlus: typeof product.proPlusPrice
        });
      });
      
      // Atualizar o estado dos produtos
      setProducts(productsData);

      // Obter os 10 produtos mais recentes (ordenados por data de criação)
      const sortedByDate = [...productsData].sort((a, b) => {
        // Função para converter diferentes formatos de data para timestamp
        const getTimestamp = (dateField: any) => {
          if (!dateField) return 0;
          
          // Se é um Timestamp do Firebase
          if (dateField.seconds) {
            return dateField.seconds * 1000;
          }
          
          // Se é um objeto Date
          if (dateField instanceof Date) {
            return dateField.getTime();
          }
          
          // Se tem método toDate (Firestore Timestamp)
          if (dateField.toDate && typeof dateField.toDate === 'function') {
            return dateField.toDate().getTime();
          }
          
          // Se é uma string de data
          if (typeof dateField === 'string') {
            return new Date(dateField).getTime();
          }
          
          // Se é um número (timestamp)
          if (typeof dateField === 'number') {
            return dateField;
          }
          
          return 0;
        };
        
        const timestampA = getTimestamp(a.createdAt);
        const timestampB = getTimestamp(b.createdAt);
        
        // Ordenar do mais recente para o mais antigo
        return timestampB - timestampA;
      });
      
      console.log('Produtos ordenados por data:', sortedByDate.slice(0, 3).map(p => ({
        name: p.name,
        createdAt: p.createdAt,
        timestamp: p.createdAt?.seconds ? p.createdAt.seconds * 1000 : 'N/A'
      })));
      
      setRecentProducts(sortedByDate.slice(0, 10));
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Erro ao carregar produtos. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId: string | null): string => {
    if (!categoryId) return 'Sem categoria';
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Sem categoria';
  };

  const isPromotionalPriceActive = (product: Product): boolean => {
    if (!product.promotionalPeriod?.startDate || !product.promotionalPeriod?.endDate) {
      return false;
    }

    const now = new Date().getTime();
    const startDate = new Date(product.promotionalPeriod.startDate).getTime();
    const endDate = new Date(product.promotionalPeriod.endDate).getTime();

    return now >= startDate && now <= endDate && product.salePrice > 0;
  };

  const getCurrentPrice = (product: Product): number => {
    // Se o produto tem preço ClubePro, SEMPRE usar esse preço
    if (product.isProPlus && product.proPlusPrice && product.proPlusPrice > 0) {
      console.log('Usando preço ClubePro:', product.proPlusPrice);
      return product.proPlusPrice;
    }
    
    // Caso NÃO tenha ClubePro, verificar se há preço promocional ativo
    if (isPromotionalPriceActive(product)) {
      console.log('Usando preço promocional:', product.salePrice);
      return product.salePrice;
    }
    
    // Se não houver promoção nem ClubePro, usar preço regular
    console.log('Usando preço regular:', product.regularPrice);
    return product.regularPrice;
  };

  const filteredProducts = products.filter(product => {
    // Se não houver termo de pesquisa, aplicar apenas o filtro de categoria
    if (!searchTerm.trim()) {
      return (!selectedCategory || selectedCategory === '' || product.category === selectedCategory);
    }
    
    // Aplicar filtro de categoria
    const matchesCategory = (!selectedCategory || selectedCategory === '' || product.category === selectedCategory);
    if (!matchesCategory) return false;
    
    // Normalizar o termo de pesquisa (remover acentos, converter para minúsculas)
    const normalizedSearchTerm = searchTerm.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    // Verificar se o produto corresponde ao termo de pesquisa em qualquer campo relevante
    const nameMatch = product.name && product.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(normalizedSearchTerm);
    const skuMatch = product.sku && product.sku.toLowerCase().includes(normalizedSearchTerm);
    const eanMatch = product.ean && product.ean.toLowerCase().includes(normalizedSearchTerm);
    const descriptionMatch = product.description && product.description.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(normalizedSearchTerm);
    
    return nameMatch || skuMatch || eanMatch || descriptionMatch;
  }).sort((a, b) => {
    // Ordenar por data de criação (mais recentes primeiro)
    const dateA = a.createdAt ? new Date(a.createdAt.seconds * 1000) : new Date(0);
    const dateB = b.createdAt ? new Date(b.createdAt.seconds * 1000) : new Date(0);
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });

  // Aplicar paginação após a filtragem e pesquisa
  const start = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(start, start + ITEMS_PER_PAGE);
  
  // Atualizar o estado de hasMore com base nos produtos filtrados
  const hasMoreAfterFilters = filteredProducts.length > start + ITEMS_PER_PAGE;
  
  // Se o estado de hasMore for diferente do calculado, atualizar
  if (hasMore !== hasMoreAfterFilters) {
    setHasMore(hasMoreAfterFilters);
  }
  
  console.log('Estado atual:', {
    totalProdutos: products.length,
    produtosFiltrados: filteredProducts.length,
    produtosNaPaginaAtual: paginatedProducts.length,
    categoriaSelecionada: selectedCategory,
    termoPesquisa: searchTerm,
    carregando: loading,
    temMais: hasMoreAfterFilters,
    paginaAtual: currentPage
  });

  const handleAddToCart = (product: ProductWithCompany) => {
    // Debug: verificar os valores do produto
    console.log('Produto completo:', product);
    console.log('proPlusPrice:', product.proPlusPrice, typeof product.proPlusPrice);
    console.log('regularPrice:', product.regularPrice, typeof product.regularPrice);
    
    // Garantir que temos um preço válido - usar sempre o preço de revenda (proPlusPrice)
    let price = 0;
    
    if (product.proPlusPrice && typeof product.proPlusPrice === 'number' && !isNaN(product.proPlusPrice)) {
      price = product.proPlusPrice;
    } else if (product.regularPrice && typeof product.regularPrice === 'number' && !isNaN(product.regularPrice)) {
      price = product.regularPrice;
    } else {
      // Fallback: tentar converter strings para números
      const proPlusNum = parseFloat(String(product.proPlusPrice));
      const regularNum = parseFloat(String(product.regularPrice));
      
      if (!isNaN(proPlusNum)) {
        price = proPlusNum;
      } else if (!isNaN(regularNum)) {
        price = regularNum;
      } else {
        price = 0; // Último recurso
      }
    }

    console.log('Preço final calculado:', price);

    if (price <= 0) {
      toast({
        title: "Erro",
        description: "Não foi possível determinar o preço do produto.",
        variant: "destructive"
      });
      return;
    }

    console.log('Adicionando ao carrinho:', {
      id: product.id,
      isProPlus: product.isProPlus,
      proPlusPrice: product.proPlusPrice,
      regularPrice: product.regularPrice,
      finalPrice: price
    });

    addItem({
      productId: product.id,
      name: product.name,
      price: price,
      mainImage: product.mainImage || '',
      quantity: 1,
      companyId: product.companyId,
      companyName: product.companyName || 'Empresa não encontrada'
    });

    toast({
      title: "Produto adicionado ao carrinho",
      description: `${product.name} foi adicionado ao seu carrinho.`,
    });
  };

  const handleViewAllProducts = () => {
    setShowAllProducts(true);
    setSelectedCategory(''); // Resetar para "Todas as categorias"
    setCurrentPage(1); // Resetar para primeira página
    setSearchTerm(''); // Limpar pesquisa
  };

  // Componente de botão de paginação personalizado
  const PaginationButton: React.FC<{
    onClick: () => void;
    disabled?: boolean;
    children: React.ReactNode;
  }> = ({ onClick, disabled, children }) => {
    const handleClick = () => {
      // Abordagem agressiva para rolar ao topo antes de mudar a página
      try {
        // Tentar todos os métodos conhecidos de rolagem
        window.scroll({ top: 0, behavior: 'auto' });
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        window.scrollTo(0, 0);
        
        // Se houver uma referência ao topo, tentar usar scrollIntoView
        if (topRef.current) {
          topRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
      } catch (e) {
        // Fallback
        window.scrollTo(0, 0);
      }
      
      // Chamar a função original após um pequeno atraso
      setTimeout(() => {
        onClick();
      }, 50);
    };

    return (
      <button
        onClick={handleClick}
        disabled={disabled}
        className={`px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
      >
        {children}
      </button>
    );
  };
  
  const handlePageChange = (pageNumber: number) => {
    // Definir a nova página atual
    setCurrentPage(pageNumber);
    
    // Abordagem direta para rolar ao topo
    try {
      // Tentar métodos diferentes para maior compatibilidade
      window.scroll({ top: 0, behavior: 'auto' });
      document.body.scrollTop = 0; // Para Safari
      document.documentElement.scrollTop = 0; // Para Chrome, Firefox, IE e Opera
      
      // Se houver uma referência ao topo, tentar usar scrollIntoView
      if (topRef.current) {
        topRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    } catch (e) {
      // Fallback se os métodos modernos falharem
      window.scrollTo(0, 0);
    }
  };
  
  // Recuperar a página salva no localStorage ao carregar
  useEffect(() => {
    const savedPage = localStorage.getItem('dropCurrentPage');
    if (savedPage) {
      setCurrentPage(parseInt(savedPage, 10));
    }
  }, []);
  
  // Efeito para carregar dados quando a página mudar
  useEffect(() => {
    loadData();
  }, [currentPage]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modal do Link XML */}
      {showXmlModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Link XML para Integração</h2>
            
            <div className="mb-6 text-gray-600">
              <p className="mb-4">
                Utilize este link no software ERP do seu site ou armazém para sincronização automática dos produtos.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4 flex items-center justify-between gap-4">
                <code className="text-sm text-gray-800 flex-1">{`${window.location.origin}/api/products/xml`}</code>
                <div className="flex gap-2">
                  <button
                    onClick={handleCopyXmlLink}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Copiar link"
                  >
                    <Copy size={18} />
                  </button>
                  <button
                    onClick={handleOpenXmlLink}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Abrir em nova janela"
                  >
                    <ExternalLink size={18} />
                  </button>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
                <strong>Atenção:</strong>
                <ul className="list-disc ml-4 mt-2 space-y-1">
                  <li>Sincronize no máximo 1 vez por hora</li>
                  <li>IPs que excederem este limite poderão ser bloqueados</li>
                  <li>Em caso de dúvidas, contacte o nosso suporte</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowXmlModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Elemento de âncora para rolagem ao topo */}
      <div id="topo" ref={topRef} style={{ position: 'absolute', top: 0, left: 0 }} />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-secondary via-secondary-light to-secondary-dark text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Área Drop
                <span className="block text-primary text-2xl lg:text-3xl mt-2">
                  Marketplace B2B para Profissionais
                </span>
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Acesso exclusivo a produtos para revenda com margens competitivas. 
                Ideal para dropshipping e compra de stock em volume.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/20 p-3 rounded-full">
                    <TrendingUp className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">Margens Altas</div>
                    <div className="text-sm text-gray-300">Até 40% de lucro</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="bg-primary/20 p-3 rounded-full">
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">Envio Rápido</div>
                    <div className="text-sm text-gray-300">24-48h úteis</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="bg-primary/20 p-3 rounded-full">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">Suporte B2B</div>
                    <div className="text-sm text-gray-300">Apoio dedicado</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={handleViewAllProducts}
                  className="bg-primary hover:bg-primary-dark text-secondary px-8 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
                >
                  Ver Todos os Produtos
                  <ArrowRight size={20} />
                </button>
                
                <button
                  onClick={() => setShowXmlModal(true)}
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 border border-white/20"
                >
                  <Link2 size={20} />
                  Integração XML
                </button>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold mb-6">Estatísticas da Plataforma</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">{products.length}</div>
                    <div className="text-gray-300">Produtos Disponíveis</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">{categories.length}</div>
                    <div className="text-gray-300">Categorias</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">24h</div>
                    <div className="text-gray-300">Processamento</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">40%</div>
                    <div className="text-gray-300">Margem Média</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ferramentas Rápidas */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Linha superior: Botões de ação */}
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={handleExportCSV}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm"
              >
                <FileDown size={18} />
                Exportar CSV
              </button>
              
              <Link
                to="/dashboard/drop/orders"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-secondary rounded-lg transition-colors text-sm"
              >
                <ClipboardList size={18} />
                Ver Encomendas
              </Link>
            </div>

            {/* Linha inferior: Filtros e Pesquisa */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:min-w-0 lg:flex-1 lg:max-w-2xl lg:ml-8">
              {/* Filtro de Categoria */}
              <div className="relative sm:min-w-48 lg:min-w-52">
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setCurrentPage(1);
                    if (!showAllProducts && e.target.value) {
                      setShowAllProducts(true);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white appearance-none text-sm"
                >
                  <option value="">Todas as Categorias</option>
                  {categories
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name} ({category.productCount})
                      </option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>

              {/* Barra de Pesquisa */}
              <div className="relative flex-1 sm:min-w-64">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                    if (!showAllProducts) {
                      setShowAllProducts(true);
                    }
                  }}
                  placeholder="Pesquisar produtos..."
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white text-sm"
                />
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Botão Voltar ao Início */}
              {showAllProducts && (
                <button
                  onClick={() => setShowAllProducts(false)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary-dark text-white rounded-lg transition-colors text-sm whitespace-nowrap"
                >
                  <ChevronLeft size={18} />
                  Voltar ao Início
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {!showAllProducts ? (
          <>
            {/* Produtos Recentes */}
            <section className="mb-16">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Produtos Recentes</h2>
                  <p className="text-gray-600">Os últimos produtos adicionados à plataforma</p>
                </div>
                <button
                  onClick={handleViewAllProducts}
                  className="text-secondary hover:text-secondary-dark font-semibold flex items-center gap-2"
                >
                  Ver Todos
                  <ArrowRight size={20} />
                </button>
              </div>
              
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                  {[...Array(10)].map((_, i) => (
                    <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-pulse">
                      <div className="aspect-square bg-gray-200"></div>
                      <div className="p-4">
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-2/3 mb-3"></div>
                        <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                  {recentProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}
            </section>

            {/* Categorias em Destaque */}
            <section className="mb-16">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Categorias em Destaque</h2>
                  <p className="text-gray-600">Categorias com mais produtos disponíveis</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {topCategories.map((category) => (
                  <div
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setShowAllProducts(true);
                    }}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="bg-primary/20 p-3 rounded-full group-hover:bg-primary/30 transition-colors">
                        <Tag className="w-6 h-6 text-secondary" />
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-secondary">{category.productCount}</div>
                        <div className="text-sm text-gray-500">produtos</div>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{category.name}</h3>
                    <p className="text-gray-600 text-sm">Explorar produtos desta categoria</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        ) : (
          <>
            {/* Cabeçalho do Catálogo */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Catálogo de Produtos</h2>
                <p className="text-gray-600">
                  {filteredProducts.length} produto{filteredProducts.length !== 1 ? 's' : ''} encontrado{filteredProducts.length !== 1 ? 's' : ''}
                  {selectedCategory && ` na categoria selecionada`}
                  {searchTerm && ` para "${searchTerm}"`}
                </p>
              </div>
            </div>

            {/* Lista de Produtos */}
            {filteredProducts.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-12">
                  {paginatedProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {/* Paginação */}
                {Math.ceil(filteredProducts.length / ITEMS_PER_PAGE) > 1 && (
                  <div className="flex justify-center items-center gap-2">
                    <PaginationButton
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={20} />
                    </PaginationButton>
                    
                    <span className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-medium">
                      Página {currentPage} de {Math.ceil(filteredProducts.length / ITEMS_PER_PAGE)}
                    </span>
                    
                    <PaginationButton
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= Math.ceil(filteredProducts.length / ITEMS_PER_PAGE)}
                    >
                      <ChevronRight size={20} />
                    </PaginationButton>
                    
                    {/* Botão de voltar ao topo */}
                    <button
                      onClick={() => {
                        localStorage.setItem('dropCurrentPage', currentPage.toString());
                        window.location.href = `/dashboard/drop?page=${currentPage}`;
                      }}
                      className="px-4 py-2 bg-primary text-white border border-primary rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center ml-4"
                      aria-label="Voltar ao topo"
                    >
                      <ArrowUp size={20} />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <Package size={64} className="mx-auto text-gray-400 mb-6" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Nenhum produto encontrado
                </h3>
                <p className="text-gray-600 mb-6">
                  Tente ajustar os seus filtros ou termos de pesquisa
                </p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('');
                  }}
                  className="bg-primary hover:bg-primary-dark text-secondary px-6 py-3 rounded-lg transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
